import React, { useEffect, useState } from 'react'
import { Container, Row, Col, OverlayTrigger, Tooltip, Button, Modal } from 'react-bootstrap'
import Card from '../../../components/Card'
import { Link } from 'react-router-dom'
import { useGetMlmAccountsMutation, useDeleteMlmAccountMutation } from '../../../api/mlmAccountsAPISlices'
import CustomPagination from '../../../components/utility/CustomPagination'
import Toaster from '../../../components/utility/Toaster'

const API_BASE_URL = 'https://api.amaze360.io'

const columns = [
    { label: "Name", key: "name" },
    { label: "Webhook URL", key: "webhook_url" },
    { label: "GHL Subaccounts", key: "ghl_auths_count" },
    { label: "Actions", key: "action" }
]

const MlmAccountsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [mlmAccounts, setMlmAccounts] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const [showToster, setShowToster] = useState(false);
    const [tosterMassage, setTosterMassage] = useState('');
    const [tosterStatus, setTosterStatus] = useState('');

    const [getMlmAccounts] = useGetMlmAccountsMutation();
    const [deleteMlmAccount] = useDeleteMlmAccountMutation();

    const token = JSON.parse(localStorage.getItem("token"));

    const getMlmAccountsData = async () => {
        setIsLoading(true)
        await getMlmAccounts({ token, currentPage })
            .unwrap()
            .then((res) => {
                setMlmAccounts(res)
                setIsLoading(false)
            })
            .catch((error) => {
                setShowToster(true)
                setTosterMassage(error?.data?.message || 'Error loading MLM accounts')
                setTosterStatus('error')
                setIsLoading(false)
            })
    }

    useEffect(() => {
        getMlmAccountsData()
    }, [currentPage])

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDeleteClick = (account) => {
        setSelectedAccount(account);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        await deleteMlmAccount({ mlmAccountId: selectedAccount.id, token })
            .unwrap()
            .then(() => {
                setShowDeleteModal(false);
                setShowToster(true)
                setTosterMassage('MLM account deleted successfully')
                setTosterStatus('success')
                getMlmAccountsData();
            })
            .catch((error) => {
                setShowDeleteModal(false);
                setShowToster(true)
                setTosterMassage(error?.data?.message || 'Error deleting MLM account')
                setTosterStatus('error')
            })
    };

    const getWebhookUrl = (webhookToken) => {
        return `${API_BASE_URL}/api/webhook/mlm/${webhookToken}`
    };

    const handleCopyWebhook = (account) => {
        const url = getWebhookUrl(account.webhook_token);
        navigator.clipboard.writeText(url).then(() => {
            setCopiedId(account.id);
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => {
            setShowToster(true)
            setTosterMassage('Failed to copy to clipboard')
            setTosterStatus('error')
        });
    };

    return (
        <>
            <Container fluid>
                <Row>
                    <Col lg="12">
                        <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" className="mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <strong>How to assign a GHL subaccount to an MLM account:</strong>
                                <ol className="mb-0 mt-1 pl-3">
                                    <li>Go to <Link to="/subaccounts-list"><strong>Subaccounts</strong></Link> in the sidebar</li>
                                    <li>Click <strong>Edit</strong> on any subaccount</li>
                                    <li>Scroll to the <strong>MLM Account</strong> section and select an account from the dropdown</li>
                                    <li>Click <strong>Save</strong></li>
                                </ol>
                            </div>
                        </div>
                        <div className="d-flex flex-wrap align-items-center justify-content-between my-schedule mb-4">
                            <div className="d-flex align-items-center justify-content-between">
                                <h4 className="font-weight-bold">MLM Accounts</h4>
                            </div>
                            <div className="create-workform">
                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                    <div className="modal-product-search d-flex">
                                        <Link
                                            to="/mlm-accounts-add"
                                            className="btn btn-primary position-relative d-flex align-items-center justify-content-between"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                            Add MLM Account
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isLoading ?
                            (<div className='text-center mt-5 pt-5'>
                                <div className="spinner-border text-warning mr-2" role="status">
                                    <span className="visually-hidden"></span>
                                </div>
                            </div>)
                            : mlmAccounts?.data?.length > 0 ?
                                <>
                                    <Row>
                                        <Col lg="12">
                                            <Card className="card-block card-stretch border-back">
                                                <Card.Body className="p-0">
                                                    <div className="table-responsive">
                                                        <table className="table data-table mb-0">
                                                            <thead className="table-color-heading">
                                                                <tr className="text-center">
                                                                    {columns.map((col, i) => (
                                                                        <th key={i} scope="col">
                                                                            {col.label}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {mlmAccounts?.data?.map((row, index) => (
                                                                    <tr key={index} className="white-space-no-wrap">
                                                                        {columns.map((col) => (
                                                                            <td className='text-center' key={col.key}>
                                                                                {col?.key === 'action' ?
                                                                                    <div className="d-flex justify-content-center align-items-center">
                                                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                                                            <Link to={`/mlm-accounts-edit/${row?.id}`}>
                                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="text-secondary mx-2" width="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                                                </svg>
                                                                                            </Link>
                                                                                        </OverlayTrigger>
                                                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                                                                                            <button
                                                                                                className="btn btn-link p-0"
                                                                                                onClick={() => handleDeleteClick(row)}
                                                                                            >
                                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="text-danger mx-2" width="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                                </svg>
                                                                                            </button>
                                                                                        </OverlayTrigger>
                                                                                    </div>
                                                                                : col?.key === 'webhook_url' ?
                                                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                                                        <code className="small text-truncate" style={{ maxWidth: 220 }} title={getWebhookUrl(row?.webhook_token)}>
                                                                                            {getWebhookUrl(row?.webhook_token)}
                                                                                        </code>
                                                                                        <OverlayTrigger placement="top" overlay={<Tooltip>{copiedId === row.id ? 'Copied!' : 'Copy'}</Tooltip>}>
                                                                                            <button
                                                                                                className="btn btn-link p-0"
                                                                                                onClick={() => handleCopyWebhook(row)}
                                                                                            >
                                                                                                {copiedId === row.id ? (
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="text-success" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                                                    </svg>
                                                                                                ) : (
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="text-secondary" width="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                                    </svg>
                                                                                                )}
                                                                                            </button>
                                                                                        </OverlayTrigger>
                                                                                    </div>
                                                                                : col?.key === 'client_id' ?
                                                                                    <code>{row?.client_id}</code>
                                                                                : col?.key === 'ghl_auths_count' ?
                                                                                    <span className="badge bg-info text-white">{row?.ghl_auths_count ?? 0}</span>
                                                                                : row?.[col?.key] || <span className="text-muted">-</span>
                                                                                }
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col lg="12" className='text-center'>
                                            <CustomPagination
                                                current_page={currentPage}
                                                last_page={mlmAccounts?.meta?.last_page}
                                                onPageChange={onPageChange}
                                            />
                                        </Col>
                                    </Row>
                                </> :
                                <div className='pt-5 mt-5 mb-5 text-center'>
                                    No MLM accounts found
                                </div>
                        }
                    </Col>
                </Row>
            </Container>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the MLM account <strong>{selectedAccount?.name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toaster status={tosterStatus} message={tosterMassage} show={showToster} setShow={setShowToster} />
        </>
    )
}

export default MlmAccountsList;
